import type { MonthlyStatus, MonthlyStatusRow, Training } from '../services/trainingLogApi';

/**
 * CSV exports of the Training Log's "All Employees" tab (role HR/Admin). The rows are the ones the
 * API already returned for scope 'all', so the export can never hold more than the page shows.
 *
 * Semicolon-separated with a UTF-8 BOM, so Excel with Swiss settings opens it in columns and keeps
 * umlauts. Numbers use a dot as decimal separator.
 */

/**
 * Course topic and organizer are free text any employee can enter, and HR opens the file in
 * Excel. A text starting with = + - @ tab or CR would run as a formula there (quoting does not
 * stop it), so it gets a leading apostrophe. Numbers stay numbers, negative ones included.
 */
function cell(v: string | number | boolean | null | undefined): string {
    if (v === null || v === undefined) return '';
    let s = typeof v === 'boolean' ? (v ? 'yes' : 'no') : String(v);
    if (typeof v === 'string' && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
    return /[";\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function toCsv(header: string[], rows: (string | number | boolean | null | undefined)[][]): string {
    return '﻿' + [header, ...rows].map(r => r.map(cell).join(';')).join('\r\n') + '\r\n';
}

const zurichDay = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Zurich', year: 'numeric', month: '2-digit', day: '2-digit',
});

/**
 * Calendar day (YYYY-MM-DD) of a timestamp in Europe/Zurich. Never slice toISOString(): that is
 * UTC, and 00:30 Zurich on 1 November would land on 31 October.
 */
const day = (iso: string | null | undefined) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : zurichDay.format(d);
};
const round2 = (n: number) => Math.round(n * 100) / 100;

/** One line per training. */
export function trainingsCsv(trainings: Training[]): string {
    return toCsv(
        ['Employee', 'Email', 'Company', 'Start date', 'End date', 'Course topic', 'Category', 'Type',
            'Hours', 'Cost EUR', 'Organizer', 'Web training', 'Supplier training'],
        trainings.map(t => [
            t.EmployeeName || t.UserEmail, t.UserEmail, t.CompanyCode ?? '', day(t.StartDate), day(t.EndDate),
            t.CourseTopic, t.Category, t.InternalExternal, t.DurationHours, t.CostsEur, t.TrainingOrganizer,
            t.IsWebTraining, t.IsSupplierTraining,
        ]),
    );
}

/** One line per employee: number of trainings, hours and cost, sorted by name. */
export function hoursPerEmployeeCsv(trainings: Training[]): string {
    const byEmail = new Map<string, { name: string; email: string; company: string; count: number; hours: number; cost: number }>();
    for (const t of trainings) {
        const key = t.UserEmail.toLowerCase();
        const e = byEmail.get(key) ?? { name: t.EmployeeName || t.UserEmail, email: t.UserEmail, company: t.CompanyCode ?? '', count: 0, hours: 0, cost: 0 };
        e.count += 1;
        e.hours += Number(t.DurationHours) || 0;
        e.cost += Number(t.CostsEur) || 0;
        byEmail.set(key, e);
    }
    const rows = [...byEmail.values()].sort((a, b) => a.name.localeCompare(b.name, 'de-CH'));
    return toCsv(
        ['Employee', 'Email', 'Company', 'Trainings', 'Hours', 'Cost EUR'],
        rows.map(e => [e.name, e.email, e.company, e.count, round2(e.hours), round2(e.cost)]),
    );
}

export const MONTHLY_STATUSES: MonthlyStatus[] = ['training', 'confirmed', 'no-response', 'not-reminded'];

export const MONTHLY_STATUS_LABEL: Record<MonthlyStatus, string> = {
    training: 'Training logged',
    confirmed: 'Confirmed no training',
    'no-response': 'No response',
    'not-reminded': 'Not reminded',
};

/** Count per status, computed from the rows themselves, so the counts always add up to rows.length. */
export function monthlyStatusCounts(rows: MonthlyStatusRow[]): Record<MonthlyStatus, number> {
    const counts: Record<MonthlyStatus, number> = { training: 0, confirmed: 0, 'no-response': 0, 'not-reminded': 0 };
    for (const r of rows) counts[r.status] += 1;
    return counts;
}

/** 'all' keeps every row. */
export function filterMonthlyStatus(rows: MonthlyStatusRow[], status: MonthlyStatus | 'all'): MonthlyStatusRow[] {
    return status === 'all' ? rows : rows.filter(r => r.status === status);
}

export function monthlyStatusName(r: MonthlyStatusRow): string {
    return [r.firstName, r.lastName].filter(Boolean).join(' ') || r.email;
}

/** One line per row passed in (the caller decides whether that is the filtered or the full list). */
export function monthlyStatusCsv(rows: MonthlyStatusRow[]): string {
    return toCsv(
        ['Employee', 'Email', 'Company', 'Status', 'Trainings', 'Confirmed', 'Reminded', 'Excluded reason'],
        rows.map(r => [
            monthlyStatusName(r), r.email, r.companyCode ?? '', MONTHLY_STATUS_LABEL[r.status], r.trainingCount,
            day(r.confirmedAt), day(r.remindedAt), r.excludedReason ?? '',
        ]),
    );
}

export function downloadCsv(csv: string, fileName: string): void {
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
}
